import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { MessageThread } from "./MessageThread";
import { brl } from "./PurchaseDialog";
import { OrderStatusBadge } from "../pages/MyOrders";
import { MessageCircle } from "lucide-react";

const STATUSES = [
  ["pending", "Pendente"], ["awaiting_info", "Aguardando informações"], ["processing", "Em andamento"],
  ["completed", "Concluído"], ["cancelled", "Cancelado (estorna o saldo)"],
];

const OrderDetail = ({ order, onClose, onChanged }) => {
  if (!order) return null;
  const send = async (fd) => {
    try { await api.post(`/admin/orders/${order.id}/messages`, fd); onChanged(); return true; }
    catch (e) { toast.error(formatApiError(e)); return false; }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/admin/orders/${order.id}`, { status }); toast.success("Status atualizado"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-order-dialog">
        <DialogHeader><DialogTitle>Pedido #{order.code} · {order.item_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><div className="text-xs text-muted-foreground">Usuário</div><div className="font-medium">{order.user_name}</div>
            <div className="text-xs text-muted-foreground">{order.user_email}</div></div>
          <div><div className="text-xs text-muted-foreground">Valor / Data</div><div className="font-mono">{brl(order.price)}</div>
            <div className="text-xs text-muted-foreground">{new Date(order.created_at).toLocaleString("pt-BR")}</div></div>
        </div>
        <div className="space-y-1">
          <div className="text-xs uppercase text-muted-foreground">Status</div>
          <Select value={order.status} onValueChange={setStatus} disabled={order.status === "cancelled"}>
            <SelectTrigger data-testid="admin-order-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUSES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`admin-order-status-${v}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs uppercase text-muted-foreground">Mensagens</div>
        <MessageThread messages={order.messages} viewer="admin" onSend={send} testid="admin-order-thread" />
      </DialogContent>
    </Dialog>
  );
};

export const AdminOrders = () => {
  const [orders, setOrders] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/orders", { params }).then((r) => setOrders(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const open = async (o) => {
    setOpenId(o.id);
    if (o.unread_admin > 0) { await api.post(`/admin/orders/${o.id}/read`).catch(() => {}); load(); }
  };
  const current = (orders || []).find((o) => o.id === openId) || null;

  return (
    <Card className="p-6" data-testid="admin-orders">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-lg font-bold">Pedidos com Saldo Consumo</div>
          <p className="text-xs text-muted-foreground">Clique em um pedido para ver e enviar mensagens ao usuário.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="sm:w-56" data-testid="admin-orders-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l.replace(" (estorna o saldo)", "")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {!orders ? <p className="text-sm text-muted-foreground">Carregando...</p>
        : orders.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-orders-empty">Nenhum pedido.</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-left py-2">Pedido</th><th className="text-left">Usuário</th><th className="text-left">Item</th>
                  <th className="text-right">Valor</th><th className="text-left pl-4">Data</th><th className="text-left">Status</th><th />
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-border/50 cursor-pointer hover:bg-muted/40" onClick={() => open(o)} data-testid={`admin-order-row-${o.id}`}>
                    <td className="py-2 font-mono">#{o.code}</td>
                    <td><div>{o.user_name}</div><div className="text-xs text-muted-foreground">{o.user_email}</div></td>
                    <td>{o.item_name}</td>
                    <td className="text-right font-mono">{brl(o.price)}<div className="text-[10px] text-muted-foreground font-sans">{o.payment_method_label}</div>
                      {o.settlement_label && <div className={`text-[10px] font-sans ${o.settlement === "released" ? "text-emerald-500" : "text-amber-500"}`} data-testid={`order-settlement-${o.id}`}>{o.partner_owner_name} · {o.settlement_label}</div>}</td>
                    <td className="pl-4 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString("pt-BR")}</td>
                    <td><OrderStatusBadge o={o} /></td>
                    <td>{o.unread_admin > 0 && <Badge className="bg-rose-500 text-white"><MessageCircle className="h-3 w-3 mr-1" />{o.unread_admin}</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <OrderDetail order={current} onClose={() => setOpenId(null)} onChanged={load} />
    </Card>
  );
};
